import type { TemplateFormat } from "@/config/brandTemplates";
import type { GalleryTemplate } from "@/stores/TemplateStore";

import React, { useCallback, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  addToast,
  Select,
  SelectItem,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useDropzone, type FileRejection } from "react-dropzone";
import { useBrand } from "@webuild-ai/react-auth";

import { useTemplateStore } from "@/stores/TemplateStore";
import { getAvailableTemplates, formatFileSize, MAX_TEMPLATE_SIZE } from "@/utils/templateEngine";
import { logger } from "@/telemetry";

const FORMAT_OPTIONS: {
  key: TemplateFormat;
  labelKey: string;
  extensions: string;
  icon: string;
}[] = [
  {
    key: "word",
    labelKey: "Word",
    extensions: ".docx / .dotx / .docm / .dotm",
    icon: "vscode-icons:file-type-word",
  },
  {
    key: "pptx",
    labelKey: "PowerPoint",
    extensions: ".pptx / .potx / .pptm / .potm",
    icon: "vscode-icons:file-type-powerpoint",
  },
  {
    key: "xlsx",
    labelKey: "Excel",
    extensions: ".xlsx / .xltx / .xlsm / .xltm",
    icon: "vscode-icons:file-type-excel",
  },
];

const ACCEPT_MAP: Record<TemplateFormat, Record<string, string[]>> = {
  word: {
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.template": [".dotx"],
    "application/vnd.ms-word.document.macroEnabled.12": [".docm"],
    "application/vnd.ms-word.template.macroEnabled.12": [".dotm"],
  },
  pptx: {
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
    "application/vnd.openxmlformats-officedocument.presentationml.template": [".potx"],
    "application/vnd.ms-powerpoint.presentation.macroEnabled.12": [".pptm"],
    "application/vnd.ms-powerpoint.template.macroEnabled.12": [".potm"],
  },
  xlsx: {
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.template": [".xltx"],
    "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    "application/vnd.ms-excel.template.macroEnabled.12": [".xltm"],
  },
};

export const TemplatesTab: React.FC = () => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { addGalleryTemplate, removeGalleryTemplate, getGalleryTemplatesForFormat } =
    useTemplateStore();

  const [selectedFormat, setSelectedFormat] = useState<TemplateFormat>("word");
  const [isUploading, setIsUploading] = useState(false);

  // Get brand defaults + admin gallery for the selected format
  const brandTemplates = getAvailableTemplates(selectedFormat, brand.name);
  const adminTemplates = getGalleryTemplatesForFormat(selectedFormat);

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        const errorCode = rejections[0].errors[0]?.code;
        let message = t("Invalid file type");

        if (errorCode === "file-too-large") {
          message = t("File is too large. Maximum size is 10 MB.");
        }

        addToast({ title: t("Upload failed"), description: message, color: "danger" });

        return;
      }

      const file = acceptedFiles[0];

      if (!file) return;

      setIsUploading(true);

      try {
        await addGalleryTemplate(selectedFormat, file, "Admin");
        addToast({
          title: t("Template added to gallery"),
          description: t("{{name}} is now available for all users.", { name: file.name }),
          color: "success",
        });
      } catch (error) {
        logger.error("Gallery template upload failed", { error: String(error) });
        addToast({
          title: t("Upload failed"),
          description: t("Could not save the template. Please try again."),
          color: "danger",
        });
      } finally {
        setIsUploading(false);
      }
    },
    [selectedFormat, addGalleryTemplate, t],
  );

  const handleRemove = useCallback(
    (template: GalleryTemplate) => {
      removeGalleryTemplate(template.id);
      addToast({
        title: t("Template removed from gallery"),
        description: t("{{name}} has been removed.", { name: template.name }),
        color: "default",
      });
    },
    [removeGalleryTemplate, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT_MAP[selectedFormat],
    maxFiles: 1,
    multiple: false,
    maxSize: MAX_TEMPLATE_SIZE,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">{t("Template Gallery")}</h3>
        <p className="text-sm text-default-500">
          {t("Manage document templates available to all users in your organisation.")}
        </p>
      </div>

      {/* Format filter */}
      <Select
        aria-label={t("Filter by format")}
        classNames={{ trigger: "max-w-xs" }}
        selectedKeys={[selectedFormat]}
        startContent={
          <Icon
            icon={FORMAT_OPTIONS.find((f) => f.key === selectedFormat)?.icon ?? ""}
            width={18}
          />
        }
        onChange={(e) => setSelectedFormat(e.target.value as TemplateFormat)}
      >
        {FORMAT_OPTIONS.map((fmt) => (
          <SelectItem key={fmt.key} startContent={<Icon icon={fmt.icon} width={18} />}>
            {t(fmt.labelKey)} ({fmt.extensions})
          </SelectItem>
        ))}
      </Select>

      {/* Brand defaults */}
      {brandTemplates.length > 0 && (
        <Card>
          <CardBody>
            <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
              <Icon className="text-primary" icon="solar:star-bold" width={16} />
              {t("Brand Defaults")}
            </h4>
            <Table
              removeWrapper
              aria-label={t("Brand default templates")}
              classNames={{ base: "min-h-0" }}
            >
              <TableHeader>
                <TableColumn>{t("Name")}</TableColumn>
                <TableColumn>{t("Description")}</TableColumn>
                <TableColumn>{t("Status")}</TableColumn>
              </TableHeader>
              <TableBody>
                {brandTemplates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <span className="font-medium">{tpl.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-default-500">{tpl.description || "—"}</span>
                    </TableCell>
                    <TableCell>
                      <Chip color="primary" size="sm" variant="flat">
                        {tpl.isDefault ? t("Default") : t("Available")}
                      </Chip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Admin gallery templates */}
      <Card>
        <CardBody>
          <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Icon className="text-secondary" icon="solar:gallery-bold" width={16} />
            {t("Admin Templates")}
            {adminTemplates.length > 0 && (
              <Chip size="sm" variant="flat">
                {adminTemplates.length}
              </Chip>
            )}
          </h4>

          {adminTemplates.length > 0 ? (
            <Table
              removeWrapper
              aria-label={t("Admin managed templates")}
              classNames={{ base: "min-h-0 mb-4" }}
            >
              <TableHeader>
                <TableColumn>{t("Name")}</TableColumn>
                <TableColumn>{t("Size")}</TableColumn>
                <TableColumn>{t("Uploaded")}</TableColumn>
                <TableColumn>{t("Actions")}</TableColumn>
              </TableHeader>
              <TableBody>
                {adminTemplates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <span className="font-medium">{tpl.name}</span>
                    </TableCell>
                    <TableCell>{formatFileSize(tpl.size)}</TableCell>
                    <TableCell>
                      {new Date(tpl.uploadedAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </TableCell>
                    <TableCell>
                      <Button
                        isIconOnly
                        aria-label={t("Remove template")}
                        color="danger"
                        size="sm"
                        variant="light"
                        onPress={() => handleRemove(tpl)}
                      >
                        <Icon icon="solar:trash-bin-trash-bold" width={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-default-400 mb-4">
              {t("No admin templates uploaded for this format yet.")}
            </p>
          )}

          {/* Upload zone */}
          <div
            {...getRootProps()}
            aria-label={t("Upload template file")}
            className={`
              flex flex-col items-center justify-center p-6 rounded-lg border-2 border-dashed
              cursor-pointer transition-all
              ${
                isDragActive
                  ? "border-primary bg-primary-50 dark:bg-primary-900/20"
                  : "border-default-300 dark:border-default-600 hover:border-primary hover:bg-default-50 dark:hover:bg-default-100/5"
              }
            `}
            role="button"
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <>
                <Icon
                  className="mb-2 text-primary animate-spin"
                  icon="solar:loading-bold"
                  width={24}
                />
                <p className="text-sm text-primary">{t("Uploading...")}</p>
              </>
            ) : (
              <>
                <Icon
                  className={`mb-2 ${isDragActive ? "text-primary" : "text-default-400"}`}
                  icon="solar:upload-bold"
                  width={24}
                />
                <p className="text-sm text-default-600">
                  {t("Drop template here or click to browse")}
                </p>
                <p className="text-xs text-default-400 mt-1">
                  {FORMAT_OPTIONS.find((f) => f.key === selectedFormat)?.extensions} &middot;{" "}
                  {t("Max 10 MB")}
                </p>
              </>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
